<template>
  <div class="overflow-hidden mt-100">
    <HeaderTwo />

    <!-- Loading state -->
    <div v-if="loading" class="portfolio-details-one mt-75 mb-150 lg-mb-100">
      <div class="container">
        <p>Loading...</p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="portfolio-details-one mt-75 mb-150 lg-mb-100">
      <div class="container">
        <p>Error: {{ error.message }}</p>
      </div>
    </div>

    <!-- Content - only render when content exists -->
    <div v-else-if="content" class="portfolio-details-one">
      <div class="solution-hero">
        <img src="../../assets/images/icon/cross.svg" alt="" height="30" width="30" />
        <h1 class="">{{ content.title }}</h1>
      </div>
      <div class="container">
        <div class="row my-2">
          <button @click="goBack" class="back-btn"><span class="arrow">‹ </span> Back</button>
        </div>
        <div class="row md-mt-50">
          <div class="col-md-6 position-relative">
            <img src="../../assets/images/icon/cross.svg" alt="" height="40" width="40" class="image-grid-cross" />
            <div class="image-grid">
              <img v-for="(pic, index) in (content.imageArray || [])"
                   :key="pic.image.asset._id"
                   :alt="pic.image?.alt"
                   class="grid-image"
                   data-aos="fade-up"
                   :data-aos-delay="(index * 300) + 300"
                   :src="urlFor(pic.image).width(250).height(250).fit('crop').auto('format').url()" />
            </div>
          </div>
          <div class="col-md-6">
            <h2 class="heading solution-heading font-bold mb-4">{{ content.heading }}</h2>
            <p class="mb-4">{{ content.excerpt }}</p>
            <ul v-if="content.featureList" class="solution-list mt-4 mb-4">
              <li v-for="feature in content.featureList" :key="feature._id" class="solution-list-item mb-2">
                <img src="../../assets/images/icon/check.svg"
                     alt=""
                     class="feature-check-icon"
                     height="20"
                     width="20">
                {{ feature }}
              </li>
            </ul>
            <h4 class="cta-line">{{ content.ctaLine }}</h4>
            <a v-if="content.button"
               :href="`mailto:sales@marshallprint.co.uk?subject=Inquiry&body=---%0AContext: ${ content.title } - Solution Hero link`"
               :target="content.button.target"
               class="btn btn-primary cta-btn mt-4">
              {{ content.button.linkText }}
            </a>
          </div>
        </div>
      </div>

      <div class="solutions-services position-relative mt-100 mb-100 pt-100 pb-100">
        <div class="container">
          <div class="row mb-6">
            <div class="col-md-6">
              <h3 class="service-heading">{{ content.serviceHeading }}</h3>
              <ul v-if="content.services">
                <li v-for="service in content.services"
                    :key="service._id"
                    class="solution-list-item solution-list-item--services mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg"
                       xml:space="preserve"
                       width="800"
                       height="800"
                       viewBox="0 0 78.369 78.369"
                       class="service-check-icon">
                      <path d="M78.049 19.015 29.458 67.606a1.094 1.094 0 0 1-1.548 0L.32 40.015a1.094 1.094 0 0 1 0-1.547l6.704-6.704a1.095 1.095 0 0 1 1.548 0l20.113 20.112 41.113-41.113a1.095 1.095 0 0 1 1.548 0l6.703 6.704a1.094 1.094 0 0 1 0 1.548z"/>
                  </svg>
                  {{ service }}
                </li>
              </ul>
              <a v-if="content.serviceCta?.href"
                 :href="`mailto:sales@marshallprint.co.uk?subject=Inquiry&body=---%0AContext: ${ content.title } - Solution Services link`"
                 :target="content.serviceCta.target"
                 class="btn btn-primary cta-btn mt-20">
                {{ content.serviceCta.linkText }}
              </a>
            </div>
            <div class="col-md-6 position-relative">
              <div class="services-image-wrapper position-relative mt-50 md-mt-0">
                <img :key="content.servicesImage.image?.asset._id"
                     :alt="content.servicesImage?.image?.alt"
                     class="services-image"
                     :src="urlFor(content.servicesImage?.image).width(400).height(400).fit('crop').auto('format').url()"
                     data-aos="fade-up"
                     data-aos-delay="300"/>
                <div class="services-shape"
                     data-aos="fade-up"
                     data-aos-delay="600"></div>
              </div>
            </div>
          </div>
          <img src="../../assets/images/icon/cross.svg" alt="" height="40" width="40" class="solutions-services-cross solutions-services-cross-one" />
          <img src="../../assets/images/icon/cross.svg" alt="" height="40" width="40" class="solutions-services-cross solutions-services-cross-two" />
        </div>
      </div>

      <div class="steps container mb-100">
        <div class="row" v-if="content.steps">
          <div class="col-12">
            <h3 class="step-heading mb-50">{{ content.stepsHeading }}</h3>
            <div class="steps-section">
              <div v-for="step in content.steps" :key="step._id" class="step-item mb-4">
                <h4 class="step-cta-heading">
                  {{ step.title }}
                  <svg width="40"
                       height="40"
                       viewBox="0 0 24 24"
                       xmlns="http://www.w3.org/2000/svg"
                       class="step-cta-chevron">
                    <path d="M10.061 19.061 17.121 12l-7.06-7.061-2.122 2.122L12.879 12l-4.94 4.939z"/>
                  </svg>
                </h4>
                <p>{{ step.excerpt }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <GalleryVideo />
    <ClientFeedback />
    <FaqAccordion />

    <FooterSeven :dark_bg="true" :dark_btn="true" />
  </div>
</template>

<script>
import HeaderTwo from '@/components/common/Headers/HeaderTwo.vue';
import FooterSeven from '@/components/common/Footers/FooterSeven.vue';
import FaqAccordion from '@/components/content/faq-accordion.vue';
import ClientFeedback from '@/components/content/client-feedback.vue';
import GalleryVideo from "@/components/content/gallery-video.vue";
import sanity from '../../../sanity.js';
import {urlFor} from "@/mixins/sanityImageMixin";
import scrollToTopMixin from '@/mixins/scrollToTopMixin'
import {useHead} from "@vueuse/head";

export default {
  name: 'DynamicCaseStudyDetails',
  components: {
    HeaderTwo,
    FooterSeven,
    FaqAccordion,
    ClientFeedback,
    GalleryVideo
  },
  data() {
    return {
      loading: true,
      content: null,
      id: this.$route.params.id,
      error: null,
      home: null,
    }
  },
  mixins: [scrollToTopMixin],
  methods: {
    urlFor,
    goBack() {
      this.$router.back();
    },
    async fetchData() {
      this.error = this.home = null;
      this.loading = true;

      try {
        const query = `*[_type == "solution" && slug.current == "${this.$route.params.id}"][0]{
          _id,
          title,
          heading,
          excerpt,
          blockText,
          slug,
          icon{
            text,
            alt,
            image{
              asset->{
                _id,
                url,
                metadata{ dimensions{ height, width } }
              }
            }
          },
          imageArray[] {
            image{
              asset->{
                _id,
                url,
                metadata{
                  dimensions{ height, width }
                }
              },
              hotspot,
              crop
            }
          },
          featureList,
          ctaLine,
          button{
            _type,
            linkText,
            href,
            target
          },
          serviceHeading,
          services,
          serviceCta{
            _type,
            linkText,
            href,
            target
          },
          servicesImage{
            image{
              asset->{
                _id,
                url,
                metadata{
                  dimensions{ height, width }
                }
              },
              hotspot,
              crop
            }
          },
          stepsHeading,
          "steps": steps[]->{
            _id,
            title,
            slug,
            excerpt,
            image{
              image{
                asset->{
                  _id,
                  url
                }
              }
            }
          },
          seo {
            title,
            description,
            keywords,
            ogImage {
              asset->{
                url
              }
            }
          }
        }`;

        const [content] = await Promise.all([
          sanity.fetch(query)
        ]);
        this.content = content || null;
        this.loading = false;
      } catch (error) {
        this.error = error;
        this.loading = false;
      }
    },
  },
  async created() {
    await this.fetchData()
    useHead({
      title: `${this.content?.title} | Marshall Print`,
      meta: [
        { name: 'description', content: this.content?.seo?.description },
        { name: 'keywords', content: this.content?.seo?.keywords },
        // { property: 'og:title', content: this.content?.seo?.title },
        // { property: 'og:description', content: this.content?.seo?.description },
        // { property: 'og:image', content: this.content?.seo?.ogImage?.asset.url },
      ]
    })
  },
}
</script>

<style scoped>
.solution-hero {
  background-color: var(--darkblue);
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  gap: 2rem;
  padding: 6rem 0;
  @media (max-width: 991px) {
    gap: 1rem;
  }
}

.solution-hero h1 {
  color: #fff;
  font-size: 2.5rem;
  font-weight: bold;
  @media (max-width: 991px) {
    font-size: 1.75rem;
  }
}
.back-btn {
  align-items: center;
  color: var(--pink);
  display: flex;
  font-size: 1.25rem;
  font-weight: bold;
  height: 2rem;
  justify-content: space-between;
  line-height: 1.25rem;
  text-decoration: none;
  width: 5.75rem;
}
.back-btn:hover {
  color: var(--darkblue);
  text-decoration: none;
}
.arrow {
  font-size: 1.75rem;
}
.solution-heading {
  font-weight: 900;
  font-size: 3rem;
  @media (max-width: 991px) {
    font-size: 2rem;
  }
}
.image-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  align-items: center;
  gap: 2rem;
  margin: 0 0 3rem 0;
  min-height: 100%;
}
.image-grid img:nth-child(1) {
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: center;
  justify-self: end;
}
.image-grid img:nth-child(2) {
  align-self: end;
  grid-column: 2;
  grid-row: 1;
}
.image-grid img:nth-child(3) {
  align-self: start;
  grid-column: 2;
  grid-row: 2;
}
.image-grid-cross {
  left: 15rem;
  position: absolute;
  top: 5rem;
}
.solution-list-item {
  display: flex;
  align-items: center;
  gap: 0.125rem;
}
.cta-line {
  color: var(--pink);
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 1rem;
}
.cta-btn {
  background-color: var(--pink);
  border: none;
  padding: 0.75rem 1rem;
}
.cta-btn:hover {
  background-color: var(--green);
  color: var(--darkblue);
}
.solutions-services {
  background-color: var(--lightgrey);
}
.service-heading {
  color: var(--pink);
  font-weight: bold;
  margin-bottom: 2rem;
  max-width: 27rem;
}
.services-image-wrapper {
  height: 27rem;
  margin: 0 auto;
  position: relative;
  width: 27rem;
  @media (max-width: 991px) {
    height: 15rem;
    top: 2rem;
    width: 15rem;
  }
}
.services-image {
  position: relative;
  height: 27rem;
  width: 27rem;
  z-index: 10;
  @media (max-width: 991px) {
    height: 15rem;
    width: 15rem;
  }
}
.services-shape {
  background-color: var(--darkblue);
  display: block;
  height: 20rem;
  position: absolute;
  top: -3rem;
  right:-3rem;
  width: 20rem;
  z-index: 0;
  @media (max-width: 991px) {
    height: 10rem;
    right: -2rem;
    top: -2rem;
    width: 10rem;
  }
}
.service-check-icon, .feature-check-icon {
  height: 1rem;
  width: 1rem;
  margin-right: 0.5rem;
}
.service-check-icon {
  fill: var(--pink);
}
.feature-check-icon {
  fill: var(--darkblue);
}
.steps-section {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;
  text-align: center;
  width: 100%;
  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
}
.step-cta-heading, .step-heading {
  display: flex;
  align-items: center;
  font-weight: bold;
  height: 2.5rem;
  justify-content: center;
  text-align: center;
}
.step-cta-chevron {
  height: 2rem;
  width: 2rem;
  margin-left: 0.5rem;
}
.step-item:nth-child(1) .step-cta-chevron {
  fill: var(--pink);
}
.step-item:nth-child(2) .step-cta-chevron {
  fill: var(--lightblue);
}
.step-item:nth-child(3) .step-cta-chevron {
  fill: var(--orange);
}
.step-item:nth-child(4) .step-cta-chevron {
  fill: var(--green);
}
.solutions-services-cross {
  height: 2rem;
  position: absolute;
  width: 2rem;
}
.solutions-services-cross-one{
  bottom: 6rem;
  right: 40%;
}
.solutions-services-cross-two {
  bottom: 1rem;
  right: 40%;
}
</style>
